import { useLayoutEffect, useRef, useState } from 'react'

import { useSettle } from './settle'

/**
 * M9.3：弹层选择类单元格（email / link / timerange / tag / week）。
 *
 * 共性：点单元格 → 在格子下方弹一个浮层（`position: fixed`，避免被横滚容器裁切），
 * 浮层内编辑完按「应用」提交；遮罩点击取消。
 *
 * 值格式（都存在 ItemValue.value 里）：
 * - email:     `{"email":"a@b.c","text":"显示名"}`
 * - link:      `{"url":"https://…","text":"显示名"}`
 * - timerange: `{"from":"YYYY-MM-DD","to":"YYYY-MM-DD"}`
 * - tag:       `["标签1","标签2"]`
 * - week:      `"2026-W42"`
 */

export interface PopupEditorProps {
  initial: string
  busy: boolean
  onCommit: (v: string) => void
  onCancel: () => void
}

/** 浮层定位：贴在锚点下方，宽度至少 minWidth */
function usePopupPos(open: boolean, minWidth = 240) {
  const anchor = useRef<HTMLSpanElement>(null)
  const [pos, setPos] = useState<{ x: number; y: number; w: number } | null>(null)
  useLayoutEffect(() => {
    if (!open) return
    const r = anchor.current?.getBoundingClientRect()
    if (r !== undefined) {
      const w = Math.max(Math.round(r.width), minWidth)
      // 夹紧到视口内：靠右的列会把弹层顶出屏幕。
      // 注意要用「面板最大宽度」而不是单元格宽度算可用空间 ——
      // 面板实际宽度由内容决定（duration 415px > 单元格 160px），用单元格宽度会算错。
      const PANEL_MAX = 420
      const maxX = Math.max(8, window.innerWidth - PANEL_MAX - 8)
      const x = Math.max(8, Math.min(Math.round(r.left), maxX))
      setPos({ x, y: Math.round(r.bottom + 8), w })
    }
  }, [open, minWidth])
  return { anchor, pos }
}

function PopupShell({
  pos,
  onClose,
  children,
  minWidth,
}: {
  pos: { x: number; y: number; w: number } | null
  onClose: () => void
  children: React.ReactNode
  minWidth?: number
}) {
  return (
    <>
      <div className="panel-backdrop" onClick={onClose} />
      <div
        className="status-panel popup-panel"
        style={{ left: pos?.x ?? 0, top: pos?.y ?? 0, minWidth: pos?.w ?? minWidth ?? 240 }}
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="status-panel__arrow" />
        {children}
      </div>
    </>
  )
}

// ============ email ============

function parseEmail(raw: string): { email: string; text: string } {
  if (raw.trim().startsWith('{')) {
    try {
      const o: unknown = JSON.parse(raw)
      if (typeof o === 'object' && o !== null) {
        const r = o as { email?: unknown; text?: unknown }
        return {
          email: typeof r.email === 'string' ? r.email : '',
          text: typeof r.text === 'string' ? r.text : '',
        }
      }
    } catch {
      /* 落到纯文本分支 */
    }
  }
  return { email: raw, text: '' }
}

/** 显示：有显示名用显示名，否则用邮箱本身 */
export function formatEmail(raw: string): string {
  const { email, text } = parseEmail(raw)
  return text !== '' ? text : email
}

export function EmailEditor({ initial, busy, onCommit, onCancel }: PopupEditorProps) {
  const parsed = parseEmail(initial)
  const [email, setEmail] = useState(parsed.email)
  const [text, setText] = useState(parsed.text)
  const { anchor, pos } = usePopupPos(true, 260)
  const s = useSettle(onCommit, onCancel)

  const apply = () => {
    const e = email.trim()
    if (e === '') return s.commit('')
    s.commit(JSON.stringify(text.trim() === '' ? { email: e } : { email: e, text: text.trim() }))
  }

  return (
    <span ref={anchor} className="popup-anchor">
      <PopupShell pos={pos} onClose={() => s.cancel()}>
        <div className="popup-body">
          <label className="popup-field">
            <span>显示名称</span>
            <input
              className="cell-input"
              value={text}
              autoFocus
              disabled={busy}
              placeholder="可选"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') apply()
                else if (e.key === 'Escape') s.cancel()
              }}
            />
          </label>
          <label className="popup-field">
            <span>邮箱</span>
            <input
              className="cell-input"
              inputMode="email"
              value={email}
              disabled={busy}
              placeholder="name@example.com"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') apply()
                else if (e.key === 'Escape') s.cancel()
              }}
            />
          </label>
          <div className="popup-foot">
            <button type="button" className="popup-btn" disabled={busy} onClick={() => s.commit('')}>
              清除
            </button>
            <button
              type="button"
              className="popup-btn popup-btn--primary"
              disabled={busy}
              onClick={apply}
            >
              应用
            </button>
          </div>
        </div>
      </PopupShell>
    </span>
  )
}

// ============ link ============

function parseLink(raw: string): { url: string; text: string } {
  if (raw.trim().startsWith('{')) {
    try {
      const o: unknown = JSON.parse(raw)
      if (typeof o === 'object' && o !== null) {
        const r = o as { url?: unknown; text?: unknown }
        return {
          url: typeof r.url === 'string' ? r.url : '',
          text: typeof r.text === 'string' ? r.text : '',
        }
      }
    } catch {
      /* 落到纯文本分支 */
    }
  }
  return { url: raw, text: '' }
}

/** 取出原始 URL（Ctrl+点击要用） */
export function linkUrl(raw: string): string {
  return parseLink(raw).url
}

export function formatLink(raw: string): string {
  const { url, text } = parseLink(raw)
  return text !== '' ? text : url
}

export function LinkEditor({ initial, busy, onCommit, onCancel }: PopupEditorProps) {
  const parsed = parseLink(initial)
  const [url, setUrl] = useState(parsed.url)
  const [text, setText] = useState(parsed.text)
  const { anchor, pos } = usePopupPos(true, 300)
  const s = useSettle(onCommit, onCancel)

  const apply = () => {
    const u = url.trim()
    if (u === '') return s.commit('')
    s.commit(JSON.stringify(text.trim() === '' ? { url: u } : { url: u, text: text.trim() }))
  }

  return (
    <span ref={anchor} className="popup-anchor">
      <PopupShell pos={pos} onClose={() => s.cancel()}>
        <div className="popup-body">
          <label className="popup-field">
            <span>地址</span>
            <input
              className="cell-input"
              value={url}
              autoFocus
              disabled={busy}
              placeholder="https://…"
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') apply()
                else if (e.key === 'Escape') s.cancel()
              }}
            />
          </label>
          <label className="popup-field">
            <span>显示文本</span>
            <input
              className="cell-input"
              value={text}
              disabled={busy}
              placeholder="可选"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') apply()
                else if (e.key === 'Escape') s.cancel()
              }}
            />
          </label>
          <div className="popup-foot">
            <button type="button" className="popup-btn" disabled={busy} onClick={() => s.commit('')}>
              清除
            </button>
            <button type="button" className="popup-btn popup-btn--primary" disabled={busy} onClick={apply}>
              应用
            </button>
          </div>
        </div>
      </PopupShell>
    </span>
  )
}

// ============ timerange ============

/** 月/日显示（同年时省略年份） */
function shortDate(iso: string, sameYear: boolean): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (m === null) return iso
  const [, y, mo, d] = m
  return sameYear ? `${Number(mo)}/${Number(d)}` : `${y}/${Number(mo)}/${Number(d)}`
}

export function formatRange(raw: string): string {
  if (raw.trim() === '') return ''
  let from = ''
  let to = ''
  try {
    const o = JSON.parse(raw) as { from?: unknown; to?: unknown }
    from = typeof o.from === 'string' ? o.from : ''
    to = typeof o.to === 'string' ? o.to : ''
  } catch {
    return raw
  }
  if (from === '' && to === '') return ''
  if (from === '') return shortDate(to, true)
  if (to === '' || to === from) return shortDate(from, true)
  const sameYear = from.slice(0, 4) === to.slice(0, 4)
  return `${shortDate(from, sameYear)} - ${shortDate(to, sameYear)}`
}

export function TimerangeEditor({ initial, busy, onCommit, onCancel }: PopupEditorProps) {
  const parsed = (() => {
    try {
      const o = JSON.parse(initial) as { from?: unknown; to?: unknown }
      return {
        from: typeof o.from === 'string' ? o.from : '',
        to: typeof o.to === 'string' ? o.to : '',
      }
    } catch {
      return { from: '', to: '' }
    }
  })()
  const [from, setFrom] = useState(parsed.from)
  const [to, setTo] = useState(parsed.to)
  const { anchor, pos } = usePopupPos(true, 280)
  const s = useSettle(onCommit, onCancel)

  const apply = () => {
    if (from === '' && to === '') return s.commit('')
    s.commit(JSON.stringify({ from: from === '' ? to : from, to: to === '' ? from : to }))
  }

  return (
    <span ref={anchor} className="popup-anchor">
      <PopupShell pos={pos} onClose={() => s.cancel()}>
        <div className="popup-body">
          <div className="popup-row">
            <label className="popup-field">
              <span>开始</span>
              <input
                type="date"
                className="cell-input"
                value={from}
                autoFocus
                disabled={busy}
                onChange={(e) => {
                  const v = e.target.value
                  setFrom(v)
                  // 开始晚于结束就顺带把结束顶过去
                  if (to !== '' && v !== '' && v > to) setTo(v)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') s.cancel()
                }}
              />
            </label>
            <label className="popup-field">
              <span>结束</span>
              <input
                type="date"
                className="cell-input"
                value={to}
                disabled={busy}
                onChange={(e) => setTo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') s.cancel()
                }}
              />
            </label>
          </div>
          <div className="popup-foot">
            <button type="button" className="popup-btn" disabled={busy} onClick={() => s.commit('')}>
              清除
            </button>
            <button type="button" className="popup-btn popup-btn--primary" disabled={busy} onClick={apply}>
              应用
            </button>
          </div>
        </div>
      </PopupShell>
    </span>
  )
}

// ============ tag ============

function parseTags(raw: string): string[] {
  if (raw.trim() === '') return []
  if (raw.trim().startsWith('[')) {
    try {
      const arr: unknown = JSON.parse(raw)
      if (Array.isArray(arr)) return arr.map((x) => String(x)).filter((x) => x !== '')
    } catch {
      /* 落到下面的分支 */
    }
  }
  return [raw]
}

export function formatTags(raw: string): string[] {
  return parseTags(raw)
}

export function TagEditor({
  initial,
  busy,
  onCommit,
  onCancel,
  suggestions = [],
}: PopupEditorProps & { suggestions?: string[] }) {
  const [tags, setTags] = useState<string[]>(() => parseTags(initial))
  const [draft, setDraft] = useState('')
  const { anchor, pos } = usePopupPos(true, 260)
  const s = useSettle(onCommit, onCancel)

  const add = (t: string) => {
    const v = t.trim()
    if (v === '' || tags.includes(v)) return
    setTags((prev) => [...prev, v])
    setDraft('')
  }
  const remove = (t: string) => setTags((prev) => prev.filter((x) => x !== t))
  const matches = suggestions
    .filter((t) => !tags.includes(t) && (draft.trim() === '' ? false : t.toLowerCase().includes(draft.trim().toLowerCase())))
    .slice(0, 6)

  return (
    <span ref={anchor} className="popup-anchor">
      <PopupShell pos={pos} onClose={() => s.cancel()}>
        <div className="popup-body">
          <div className="tag-chosen">
            {tags.length === 0 && <span className="dim">还没有标签</span>}
            {tags.map((t) => (
              <span key={t} className="tag-chip">
                {t}
                <button
                  type="button"
                  className="tag-chip__x"
                  aria-label={`移除 ${t}`}
                  onClick={() => remove(t)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <input
            className="cell-input"
            value={draft}
            autoFocus
            disabled={busy}
            placeholder="输入标签后回车"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (draft.trim() !== '') add(draft)
              } else if (e.key === 'Escape') s.cancel()
              else if (e.key === 'Backspace' && draft === '' && tags.length > 0) remove(tags[tags.length - 1] ?? '')
            }}
          />
          {matches.length > 0 && (
            <div className="tag-suggest">
              {matches.map((t) => (
                <button key={t} type="button" className="tag-suggest__item" onClick={() => add(t)}>
                  {t}
                </button>
              ))}
            </div>
          )}
          <div className="popup-foot">
            <button type="button" className="popup-btn" disabled={busy} onClick={() => s.commit('')}>
              清除
            </button>
            <button
              type="button"
              className="popup-btn popup-btn--primary"
              disabled={busy}
              onClick={() => s.commit(tags.length === 0 ? '' : JSON.stringify(tags))}
            >
              应用
            </button>
          </div>
        </div>
      </PopupShell>
    </span>
  )
}

// ============ week ============

function parseWeek(raw: string): { year: number; week: number } {
  const m = /^(\d{4})-W(\d{1,2})$/.exec(raw.trim())
  if (m === null) {
    const d = new Date()
    return { year: d.getFullYear(), week: isoWeek(d).week }
  }
  return { year: Number(m[1]), week: Number(m[2]) }
}

/** ISO 8601 周号（周一为一周开始，含 1 月 4 日的那周是第 1 周） */
export function isoWeek(d: Date): { year: number; week: number } {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const day = t.getUTCDay() || 7
  t.setUTCDate(t.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1))
  const week = Math.ceil(((t.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return { year: t.getUTCFullYear(), week }
}

/** 取出原始邮箱（mailto 要用） */
export function emailAddress(raw: string): string {
  return parseEmail(raw).email
}

export function formatWeek(raw: string): string {
  if (raw.trim() === '') return ''
  const { year, week } = parseWeek(raw)
  return `${year} 第 ${week} 周`
}

export function WeekEditor({ initial, busy, onCommit, onCancel }: PopupEditorProps) {
  const parsed = parseWeek(initial)
  const [year, setYear] = useState(parsed.year)
  const [week, setWeek] = useState(parsed.week)
  const { anchor, pos } = usePopupPos(true, 240)
  const s = useSettle(onCommit, onCancel)

  const step = (delta: number) => {
    let w = week + delta
    let y = year
    if (w < 1) {
      y -= 1
      w = 52
    } else if (w > 53) {
      y += 1
      w = 1
    }
    setYear(y)
    setWeek(w)
  }

  return (
    <span ref={anchor} className="popup-anchor">
      <PopupShell pos={pos} onClose={() => s.cancel()}>
        <div className="popup-body">
          <div className="week-row">
            <button type="button" className="week-step" disabled={busy} onClick={() => step(-1)} aria-label="上一周">
              ‹
            </button>
            <input
              type="number"
              className="cell-input week-year"
              value={year}
              min={1970}
              max={2999}
              disabled={busy}
              onChange={(e) => setYear(Number(e.target.value) || year)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') s.cancel()
              }}
            />
            <span className="week-sep">年</span>
            <input
              type="number"
              className="cell-input week-num"
              value={week}
              min={1}
              max={53}
              disabled={busy}
              onChange={(e) => setWeek(Math.max(1, Math.min(53, Number(e.target.value) || 1)))}
              onKeyDown={(e) => {
                if (e.key === 'Escape') s.cancel()
              }}
            />
            <span className="week-sep">周</span>
            <button type="button" className="week-step" disabled={busy} onClick={() => step(1)} aria-label="下一周">
              ›
            </button>
          </div>
          <div className="popup-foot">
            <button type="button" className="popup-btn" disabled={busy} onClick={() => s.commit('')}>
              清除
            </button>
            <button
              type="button"
              className="popup-btn popup-btn--primary"
              disabled={busy}
              onClick={() => s.commit(`${year}-W${String(week).padStart(2, '0')}`)}
            >
              应用
            </button>
          </div>
        </div>
      </PopupShell>
    </span>
  )
}

// ============ duration（计时） ============

interface Segment {
  s: string
  e: string
}

/** 解析成区间数组；单个非法值返回空数组 */
function parseSegments(raw: string): Segment[] {
  if (raw.trim() === '') return []
  if (!raw.trim().startsWith('[')) return []
  try {
    const arr: unknown = JSON.parse(raw)
    if (!Array.isArray(arr)) return []
    const out: Segment[] = []
    for (const item of arr) {
      if (typeof item !== 'object' || item === null) continue
      const o = item as { s?: unknown; e?: unknown }
      out.push({
        s: typeof o.s === 'string' ? o.s : '',
        e: typeof o.e === 'string' ? o.e : '',
      })
    }
    return out
  } catch {
    return []
  }
}

/** 把 "YYYY-MM-DD HH:mm" / "YYYY-MM-DDTHH:mm" 转成毫秒；失败返回 NaN */
function segMs(v: string): number {
  if (v === '') return Number.NaN
  const t = v.replace(' ', 'T')
  const ms = Date.parse(t.length === 10 ? t + 'T00:00' : t)
  return Number.isNaN(ms) ? Number.NaN : ms
}

/** 总时长（毫秒），忽略未闭合的段落 */
function totalMs(segs: Segment[]): number {
  let sum = 0
  for (const g of segs) {
    const a = segMs(g.s)
    const b = segMs(g.e)
    if (!Number.isNaN(a) && !Number.isNaN(b) && b > a) sum += b - a
  }
  return sum
}

/** 显示：`1h 30m` 风格，不足 1 分钟显示 `<1m` */
export function formatDuration(raw: string): string {
  const segs = parseSegments(raw)
  if (segs.length === 0) return ''
  const ms = totalMs(segs)
  if (ms <= 0) return '0m'
  const minutes = Math.floor(ms / 60000)
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0 && m === 0) return '<1m'
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`
}

export function DurationEditor({ initial, busy, onCommit, onCancel }: PopupEditorProps) {
  const [segs, setSegs] = useState<Segment[]>(() => {
    const parsed = parseSegments(initial)
    return parsed.length === 0 ? [{ s: '', e: '' }] : parsed
  })
  const { anchor, pos } = usePopupPos(true, 340)
  const s = useSettle(onCommit, onCancel)

  const patchSeg = (i: number, next: Partial<Segment>) =>
    setSegs((prev) => prev.map((g, j) => (j === i ? { ...g, ...next } : g)))

  const apply = () => {
    const kept = segs.filter((g) => g.s !== '' || g.e !== '')
    s.commit(kept.length === 0 ? '' : JSON.stringify(kept))
  }

  return (
    <span ref={anchor} className="popup-anchor">
      <PopupShell pos={pos} onClose={() => s.cancel()}>
        <div className="popup-body">
          {segs.map((g, i) => (
            <div key={i} className="dur-row">
              <input
                type="datetime-local"
                className="cell-input"
                value={g.s}
                disabled={busy}
                aria-label={`第 ${i + 1} 段开始`}
                onChange={(e) => patchSeg(i, { s: e.target.value })}
              />
              <span className="dur-sep">→</span>
              <input
                type="datetime-local"
                className="cell-input"
                value={g.e}
                disabled={busy}
                aria-label={`第 ${i + 1} 段结束`}
                onChange={(e) => patchSeg(i, { e: e.target.value })}
              />
              <button
                type="button"
                className="dur-del"
                aria-label="删除该段"
                disabled={busy}
                onClick={() => setSegs((prev) => prev.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </div>
          ))}
          <div className="dur-foot">
            <button
              type="button"
              className="popup-btn"
              disabled={busy}
              onClick={() => setSegs((prev) => [...prev, { s: '', e: '' }])}
            >
              + 添加一段
            </button>
            <span className="dur-total">合计 {formatDuration(JSON.stringify(segs)) || '0m'}</span>
          </div>
          <div className="popup-foot">
            <button type="button" className="popup-btn" disabled={busy} onClick={() => s.commit('')}>
              清除
            </button>
            <button type="button" className="popup-btn popup-btn--primary" disabled={busy} onClick={apply}>
              应用
            </button>
          </div>
        </div>
      </PopupShell>
    </span>
  )
}